import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Stack,
  LinearProgress,
  Chip,
  IconButton,
  Collapse,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as EmptyCircleIcon,
  Delete as DeleteIcon,
  FileDownload as FileDownloadIcon,
  Lock as LockIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  SBA_CHECKLIST,
  FAST_FUNDING_CHECKLIST,
  FAST_FUNDING_UPSELLS,
} from '../constants/documentRequirements';
import BankStatementUploader from '../components/BankStatementUploader';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const FINANCING_APP_URL = import.meta.env.VITE_FINANCING_APP_URL || 'https://app.corebusinesscapital.com';
const PFS_URL = import.meta.env.VITE_PFS_URL || 'https://pfs.corebusinesscapital.com';
const CORELOANHUB_URL = import.meta.env.VITE_CORELOANHUB_URL || 'https://coreloanhub.com';

const getMonthName = (month) => {
  const names = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return names[parseInt(month, 10) - 1] || '';
};

const formatFileDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString();
};

// ============================================================================
// Main Component
// ============================================================================

const Documents = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // Data
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasApplication, setHasApplication] = useState(false);
  const [hasPFS, setHasPFS] = useState(false);
  const [hasDebtSchedule, setHasDebtSchedule] = useState(false);
  const navigate = useNavigate();

  // Which package the user selected: 'sba' | 'fast' | null (chooser)
  const [packagePath, setPackagePath] = useState(() => {
    const stored = localStorage.getItem('docPackagePath');
    return (stored === 'sba' || stored === 'fast') ? stored : null;
  });

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('');

  // UI
  const [expandedItems, setExpandedItems] = useState({});
  const [loadingApp, setLoadingApp] = useState(null);
  const fileInputRef = useRef(null);
  const bankUploaderRef = useRef(null);

  // ---------- Data fetching ----------

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchDocuments(), fetchFormStatus(), fetchDebtScheduleStatus()]);
    setLoading(false);
  };

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocuments(res.data.data || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      toast.error(t('docs.failedToLoad', 'Failed to load documents'));
    }
  };

  const fetchFormStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/requirements/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setHasApplication(res.data.data.hasApplication || false);
        setHasPFS(res.data.data.hasPFS || false);
      }
    } catch (err) {
      console.error('Error fetching form status:', err);
    }
  };

  const fetchDebtScheduleStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/debt-schedule`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const schedules = res.data?.data || [];
      setHasDebtSchedule(schedules.length > 0 && schedules[0].debts?.length > 0);
    } catch (err) {
      console.error('Error fetching debt schedule status:', err);
    }
  };

  // ---------- Upload handling ----------

  const openUploadFor = (docType) => {
    // Business bank statements go through the multi-file uploader (month matching + coverage).
    if (docType === 'business_bank_statement') {
      bankUploaderRef.current?.open();
      return;
    }
    setUploadDocType(docType);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.includes('pdf') && !file.type.includes('image')) {
      toast.error(t('docs.onlyPdfAndImage', 'Only PDF and image files are allowed'));
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error(t('docs.fileTooLarge', 'File size must be less than 50MB'));
      return;
    }
    doUpload(file, uploadDocType);
  };

  const doUpload = async (file, docType) => {
    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('document', file);
      formData.append('documentType', docType);
      formData.append('description', docType);
      await axios.post(`${API_URL}/api/documents/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('docs.uploadSuccess', 'Document uploaded successfully!'));
      fetchDocuments();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.message || t('docs.failedToUpload', 'Failed to upload document'));
    } finally {
      setUploading(false);
    }
  };

  // ---------- Download / Delete ----------

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/documents/${doc._id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = res.data.downloadUrl || res.data.url;
      if (url) window.open(url, '_blank');
    } catch {
      toast.error(t('docs.failedToDownload', 'Failed to download document'));
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm(t('docs.deleteConfirm', 'Are you sure you want to delete this document?'))) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('docs.deleteSuccess', 'Document deleted successfully'));
      fetchDocuments();
    } catch {
      toast.error(t('docs.failedToDelete', 'Failed to delete document'));
    }
  };

  // ---------- External app launch (SSO) ----------

  const launchExternalApp = async (appType) => {
    setLoadingApp(appType);
    try {
      const baseUrl = appType === 'pfs' ? PFS_URL : FINANCING_APP_URL;
      const url = new URL(baseUrl);
      const returnParam = appType === 'pfs' ? 'pfs_submitted=true' : 'submitted=true';
      url.searchParams.append('returnUrl', `${CORELOANHUB_URL}/dashboard?${returnParam}&source=${appType === 'pfs' ? 'pfs-copilot' : 'financing-app'}`);
      if (user?.email) url.searchParams.append('email', user.email);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(`${API_URL}/api/auth/generate-single-use-token`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) url.searchParams.append('authToken', res.data.token);
      } catch { /* continue without token */ }
      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } finally {
      setLoadingApp(null);
    }
  };

  // ---------- UI helpers ----------

  const selectPackage = (pkg) => {
    setPackagePath(pkg);
    if (pkg) {
      localStorage.setItem('docPackagePath', pkg);
    } else {
      localStorage.removeItem('docPackagePath');
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getItemStatus = (item) => {
    if (item.kind === 'form') {
      if (item.comingSoon) return 'locked';
      if (item.internalPath) return hasDebtSchedule ? 'complete' : 'not-started';
      if (item.appType === 'application') return hasApplication ? 'complete' : 'not-started';
      if (item.appType === 'pfs') return hasPFS ? 'complete' : 'not-started';
      return 'not-started';
    }
    const uploaded = documents.filter((d) => d.documentType === item.docType).length;
    if (uploaded >= item.required) return 'complete';
    if (uploaded > 0) return 'in-progress';
    return 'not-started';
  };

  const getUploadedForType = (docType) => documents.filter((d) => d.documentType === docType);

  const computeProgress = (checklist) => {
    let total = 0;
    let done = 0;
    checklist.forEach((item) => {
      if (item.comingSoon) return;
      total++;
      if (getItemStatus(item) === 'complete') done++;
    });
    return { total, done, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
  };

  // ---------- Render ----------

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 2, md: 3 } }}>
      <input type="file" accept=".pdf,image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} />

      {/* ====== BANK STATEMENTS (always first — required for every funding path) ====== */}
      <Box sx={{ mb: 4 }}>
        <BankStatementUploader ref={bankUploaderRef} documents={documents} onUploaded={fetchDocuments} />
      </Box>

      {/* ====== PACKAGE CHOOSER ====== */}
      {!packagePath && (
        <PackageChooser onSelect={selectPackage} />
      )}

      {/* ====== SBA PATH ====== */}
      {packagePath === 'sba' && (
        <SbaPackageView
          checklist={SBA_CHECKLIST}
          progress={computeProgress(SBA_CHECKLIST)}
          getItemStatus={getItemStatus}
          getUploadedForType={getUploadedForType}
          expandedItems={expandedItems}
          toggleExpand={toggleExpand}
          openUploadFor={openUploadFor}
          uploading={uploading}
          uploadDocType={uploadDocType}
          loadingApp={loadingApp}
          launchExternalApp={launchExternalApp}
          handleDownload={handleDownload}
          handleDelete={handleDelete}
          onSwitchPath={() => selectPackage(null)}
          onNavigate={navigate}
        />
      )}

      {/* ====== FAST FUNDING PATH ====== */}
      {packagePath === 'fast' && (
        <FastFundingView
          checklist={FAST_FUNDING_CHECKLIST}
          upsells={FAST_FUNDING_UPSELLS}
          progress={computeProgress(FAST_FUNDING_CHECKLIST)}
          documents={documents}
          getItemStatus={getItemStatus}
          getUploadedForType={getUploadedForType}
          expandedItems={expandedItems}
          toggleExpand={toggleExpand}
          openUploadFor={openUploadFor}
          uploading={uploading}
          uploadDocType={uploadDocType}
          loadingApp={loadingApp}
          launchExternalApp={launchExternalApp}
          handleDownload={handleDownload}
          handleDelete={handleDelete}
          onSwitchPath={() => selectPackage(null)}
          onNavigate={navigate}
        />
      )}
    </Container>
  );
};

// ============================================================================
// Package Chooser  (shown when no path is selected)
// ============================================================================

const PackageChooser = ({ onSelect }) => {
  const { t } = useTranslation();
  return (
  <Stack spacing={3}>
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', md: '2rem' } }}>
        {t('documentsPage.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {t('documentsPage.subtitle')}
      </Typography>
    </Box>

    <Stack spacing={2}>
      {/* SBA Loan card */}
      <Card
        onClick={() => onSelect('sba')}
        sx={{
          cursor: 'pointer',
          border: 2,
          borderColor: 'primary.main',
          transition: 'all 0.2s',
          '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
        }}
      >
        <CardContent sx={{ py: 3, px: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ fontSize: 40, lineHeight: 1 }}>{"\u{1F3DB}\uFE0F"}</Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('documentsPage.sbaLoanPackage')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('documentsPage.sbaLoanDesc')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {t('documentsPage.sbaLoanItems')}
              </Typography>
            </Box>
            <ArrowForwardIcon color="primary" />
          </Stack>
        </CardContent>
      </Card>

      {/* Fast Business Funding card */}
      <Card
        onClick={() => onSelect('fast')}
        sx={{
          cursor: 'pointer',
          border: 2,
          borderColor: 'success.main',
          transition: 'all 0.2s',
          '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
        }}
      >
        <CardContent sx={{ py: 3, px: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ fontSize: 40, lineHeight: 1 }}>{"\u26A1"}</Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('documentsPage.fastBusinessFunding')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('documentsPage.fastFundingDesc')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {t('documentsPage.fastFundingItems')}
              </Typography>
            </Box>
            <ArrowForwardIcon color="success" />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  </Stack>
  );
};

// ============================================================================
// SBA Package View
// ============================================================================

const SbaPackageView = ({
  checklist, progress, getItemStatus, getUploadedForType,
  expandedItems, toggleExpand, openUploadFor, uploading, uploadDocType,
  loadingApp, launchExternalApp, handleDownload, handleDelete, onSwitchPath, onNavigate,
}) => {
  const { t } = useTranslation();
  return (
  <Stack spacing={3}>
    {/* Header */}
    <Box>
      <Button size="small" startIcon={<ArrowBackIcon />} onClick={onSwitchPath}
        sx={{ mb: 1, textTransform: 'none', color: 'text.secondary' }}>
        {t('documentsPage.switchFundingPath')}
      </Button>
      <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', md: '2rem' } }}>
        {t('documentsPage.sbaLoanPackage')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {t('documentsPage.completeEachItem')}
      </Typography>
    </Box>

    {/* Progress */}
    <ProgressCard progress={progress} completeMsg={t('documentsPage.sbaComplete')} />

    {/* Forms */}
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1.5, mb: 1, display: 'block' }}>
        {t('documentsPage.formsToComplete')}
      </Typography>
      <Stack spacing={1.5}>
        {checklist.filter((i) => i.kind === 'form').map((item) => (
          <ChecklistRow key={item.id} item={item} status={getItemStatus(item)} loadingApp={loadingApp} onLaunch={launchExternalApp} onNavigate={onNavigate} />
        ))}
      </Stack>
    </Box>

    {/* Documents */}
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1.5, mb: 1, display: 'block' }}>
        {t('documentsPage.documentsToUpload')}
      </Typography>
      <Stack spacing={1.5}>
        {checklist.filter((i) => i.kind === 'upload').map((item) => {
          const status = getItemStatus(item);
          const uploadedDocs = getUploadedForType(item.docType);
          const isExpanded = expandedItems[item.id] ?? (status === 'in-progress');
          return (
            <Box key={item.id}>
              <UploadChecklistRow item={item} status={status}
                uploadedCount={uploadedDocs.length}
                isExpanded={isExpanded && uploadedDocs.length > 0}
                onToggle={() => toggleExpand(item.id)}
                onUpload={() => openUploadFor(item.docType)}
                uploading={uploading && uploadDocType === item.docType} />
              <UploadedFilesCollapse docs={uploadedDocs} isExpanded={isExpanded}
                required={item.required} handleDownload={handleDownload} handleDelete={handleDelete} />
            </Box>
          );
        })}
      </Stack>
    </Box>
  </Stack>
  );
};

// ============================================================================
// Fast Funding View
// ============================================================================

const FastFundingView = ({
  checklist, upsells, progress, documents,
  getItemStatus, getUploadedForType,
  expandedItems, toggleExpand, openUploadFor, uploading, uploadDocType,
  loadingApp, launchExternalApp, handleDownload, handleDelete, onSwitchPath, onNavigate,
}) => {
  const { t } = useTranslation();
  const coreComplete = progress.pct === 100;

  return (
    <Stack spacing={3}>
      {/* Header */}
      <Box>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={onSwitchPath}
          sx={{ mb: 1, textTransform: 'none', color: 'text.secondary' }}>
          {t('documentsPage.switchFundingPath')}
        </Button>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            {t('documentsPage.fastBusinessFunding')}
          </Typography>
          <Box sx={{ fontSize: 28 }}>{"\u26A1"}</Box>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('documentsPage.completeFastItems')}
        </Typography>
      </Box>

      {/* Progress */}
      <ProgressCard progress={progress} completeMsg={t('documentsPage.fastComplete')} color="success" />

      {/* Core checklist */}
      <Stack spacing={1.5}>
        {checklist.map((item) => {
          if (item.kind === 'form') {
            return <ChecklistRow key={item.id} item={item} status={getItemStatus(item)} loadingApp={loadingApp} onLaunch={launchExternalApp} onNavigate={onNavigate} />;
          }
          const status = getItemStatus(item);
          const uploadedDocs = getUploadedForType(item.docType);
          const isExpanded = expandedItems[item.id] ?? (status === 'in-progress');
          return (
            <Box key={item.id}>
              <UploadChecklistRow item={item} status={status}
                uploadedCount={uploadedDocs.length}
                isExpanded={isExpanded && uploadedDocs.length > 0}
                onToggle={() => toggleExpand(item.id)}
                onUpload={() => openUploadFor(item.docType)}
                uploading={uploading && uploadDocType === item.docType} />
              <UploadedFilesCollapse docs={uploadedDocs} isExpanded={isExpanded}
                required={item.required} handleDownload={handleDownload} handleDelete={handleDelete} />
            </Box>
          );
        })}
      </Stack>

      {/* Upsell section --- only visible once core items are done */}
      {coreComplete && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t('documentsPage.unlockMoreOptions')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('documentsPage.unlockMoreDesc')}
            </Typography>
            <Stack spacing={2}>
              {upsells.map((card) => {
                const uploaded = documents.filter((d) => d.documentType === card.docType);
                const isDone = uploaded.length >= card.required;
                return (
                  <UpsellCard key={card.id} card={card} uploaded={uploaded} isDone={isDone}
                    onUpload={() => openUploadFor(card.docType)}
                    uploading={uploading && uploadDocType === card.docType}
                    handleDownload={handleDownload} handleDelete={handleDelete} />
                );
              })}
            </Stack>
          </Box>
        </>
      )}

      {/* Nudge toward SBA if they want even more */}
      {coreComplete && (
        <Card sx={{ border: 1, borderColor: 'divider', bgcolor: '#f8f9fc', mt: 1 }}>
          <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ fontSize: 30, lineHeight: 1 }}>{"\u{1F3DB}\uFE0F"}</Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {t('documentsPage.needSbaLoan')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('documentsPage.switchToSba')}
                </Typography>
              </Box>
              <Button variant="outlined" size="small" onClick={onSwitchPath}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                {t('documentsPage.viewSbaPackage')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

// ============================================================================
// Shared sub-components
// ============================================================================

/* Progress card used by both paths */
const ProgressCard = ({ progress, completeMsg, color = 'primary' }) => {
  const { t } = useTranslation();
  const isComplete = progress.pct === 100;
  const barColor = isComplete ? 'success' : color;
  return (
    <Card sx={{ border: 2, borderColor: isComplete ? 'success.main' : `${color}.main` }}>
      <CardContent sx={{ py: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('documentsPage.itemsComplete', { done: progress.done, total: progress.total })}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: isComplete ? 'success.main' : `${color}.main` }}>
            {progress.pct}%
          </Typography>
        </Stack>
        <LinearProgress variant="determinate" value={progress.pct}
          sx={{ height: 10, borderRadius: 4, mt: 1.5 }} color={barColor} />
        {isComplete && (
          <Typography variant="body2" color="success.main" sx={{ mt: 1, fontWeight: 600 }}>
            {completeMsg}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

/* Form checklist row */
const ChecklistRow = ({ item, status, loadingApp, onLaunch, onNavigate }) => {
  const { t } = useTranslation();
  const isComplete = status === 'complete';
  const isLocked = status === 'locked';
  const isInternal = !!item.internalPath;
  const isLoading = loadingApp === item.appType;

  return (
    <Card sx={{
      borderLeft: 4,
      borderColor: isComplete ? 'success.main' : isLocked ? 'grey.300' : 'primary.main',
      bgcolor: isComplete ? '#f0faf3' : isLocked ? '#fafafa' : '#fff',
      opacity: isLocked ? 0.65 : 1,
    }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {isComplete ? <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
            : isLocked ? <LockIcon sx={{ color: 'grey.400', fontSize: 28 }} />
            : <EmptyCircleIcon sx={{ color: 'grey.400', fontSize: 28 }} />}
          <Box sx={{ fontSize: 24, lineHeight: 1 }}>{item.icon}</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{t(`checklistLabels.${item.id}`, item.label)}</Typography>
            <Typography variant="caption" color="text.secondary">{t(`checklistLabels.${item.id}_desc`, item.description)}</Typography>
          </Box>
          {isComplete && <Chip label={t('documentsPage.complete')} size="small" color="success" variant="outlined" />}
          {isLocked && <Chip label={t('documentsPage.comingSoon')} size="small" variant="outlined" />}
          {!isComplete && !isLocked && isInternal && (
            <Button variant="contained" size="small" onClick={() => onNavigate(item.internalPath)}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
              sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}>
              {t('documentsPage.start')}
            </Button>
          )}
          {!isComplete && !isLocked && !isInternal && (
            <Button variant="contained" size="small" onClick={() => onLaunch(item.appType)} disabled={isLoading}
              endIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <OpenInNewIcon sx={{ fontSize: 16 }} />}
              sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}>
              {isLoading ? t('documentsPage.opening') : t('documentsPage.start')}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

/* Upload checklist row */
const UploadChecklistRow = ({ item, status, uploadedCount, isExpanded, onToggle, onUpload, uploading }) => {
  const { t } = useTranslation();
  const isComplete = status === 'complete';
  const isPartial = status === 'in-progress';

  return (
    <Card sx={{
      borderLeft: 4,
      borderColor: isComplete ? 'success.main' : isPartial ? 'warning.main' : 'grey.300',
      bgcolor: isComplete ? '#f0faf3' : '#fff',
    }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {isComplete ? <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
            : <EmptyCircleIcon sx={{ color: isPartial ? 'warning.main' : 'grey.400', fontSize: 28 }} />}
          <Box sx={{ fontSize: 24, lineHeight: 1 }}>{item.icon}</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{t(`checklistLabels.${item.id}`, item.label)}</Typography>
              <Chip label={`${Math.min(uploadedCount, item.required)}/${item.required}`} size="small"
                color={isComplete ? 'success' : isPartial ? 'warning' : 'default'} variant="outlined"
                sx={{ height: 22, fontSize: '0.75rem' }} />
            </Stack>
            <Typography variant="caption" color="text.secondary">{t(`checklistLabels.${item.id}_desc`, item.description)}</Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {uploadedCount > 0 && (
              <IconButton size="small" onClick={onToggle}>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
            {!isComplete && (
              <Button variant={isPartial ? 'contained' : 'outlined'} size="small" onClick={onUpload} disabled={uploading}
                startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon sx={{ fontSize: 16 }} />}
                sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}>
                {uploading ? t('documentsPage.uploadingBtn') : t('documentsPage.uploadBtn')}
              </Button>
            )}
            {isComplete && <Chip label={t('documentsPage.complete')} size="small" color="success" variant="outlined" />}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

/* Collapsible uploaded-files list used under checklist rows */
const UploadedFilesCollapse = ({ docs, isExpanded, required, handleDownload, handleDelete }) => {
  const { t } = useTranslation();
  return (
  <Collapse in={isExpanded && docs.length > 0}>
    <Stack spacing={0.5} sx={{ pl: { xs: 5, sm: 6.5 }, pr: 1, pb: 1 }}>
      {docs.map((doc) => (
        <Stack key={doc._id} direction="row" alignItems="center" spacing={1}
          sx={{ py: 0.5, px: 1.5, bgcolor: '#f8fafb', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
          <Typography variant="caption" sx={{ flex: 1, fontWeight: 500, wordBreak: 'break-all' }}>
            {doc.documentName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {doc.statementMonth ? `${getMonthName(doc.statementMonth)} ${doc.statementYear}` : ''}
            {doc.fileSize ? ` ${String.fromCharCode(183)} ${(doc.fileSize / 1024 / 1024).toFixed(1)} MB` : ''}
            {formatFileDate(doc.uploadedAt) ? ` ${String.fromCharCode(183)} ${formatFileDate(doc.uploadedAt)}` : ''}
          </Typography>
          <IconButton size="small" onClick={() => handleDownload(doc)}>
            <FileDownloadIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(doc._id)} color="error">
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      ))}
      {docs.length < required && (
        <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600, pl: 1 }}>
          {t('documentsPage.uploadMore', { count: required - docs.length })}
        </Typography>
      )}
    </Stack>
  </Collapse>
  );
};

/* Upsell card for Fast Funding add-ons */
const UpsellCard = ({ card, uploaded, isDone, onUpload, uploading, handleDownload, handleDelete }) => {
  const { t } = useTranslation();
  return (
  <Card sx={{
    border: 1,
    borderColor: isDone ? 'success.main' : 'divider',
    bgcolor: isDone ? '#f0faf3' : '#fff',
    transition: 'all 0.2s',
  }}>
    <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box sx={{ fontSize: 32, lineHeight: 1, mt: 0.25 }}>{card.icon}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.25 }}>{t(`checklistLabels.${card.id}_headline`, card.headline)}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t(`checklistLabels.${card.id}_desc`, card.description)}</Typography>

          {/* Uploaded files inline */}
          {uploaded.length > 0 && (
            <Stack spacing={0.5} sx={{ mb: 1 }}>
              {uploaded.map((doc) => (
                <Stack key={doc._id} direction="row" alignItems="center" spacing={1}
                  sx={{ py: 0.25, px: 1, bgcolor: '#f8fafb', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                  <Typography variant="caption" sx={{ flex: 1, fontWeight: 500, wordBreak: 'break-all' }}>
                    {doc.documentName}
                  </Typography>
                  <IconButton size="small" onClick={() => handleDownload(doc)}>
                    <FileDownloadIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(doc._id)} color="error">
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}

          {!isDone && (
            <Button variant="outlined" size="small" onClick={onUpload} disabled={uploading}
              startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none' }}>
              {uploading ? t('documentsPage.uploadingBtn') : t(`checklistLabels.${card.id}_cta`, card.cta)}
            </Button>
          )}
          {isDone && <Chip label={t('documentsPage.complete')} size="small" color="success" variant="outlined" />}
        </Box>
      </Stack>
    </CardContent>
  </Card>
  );
};

export default Documents;
